<template>
  <ValidationObserver ref="messageTemplateForm">
    <ValidationProvider v-slot="{ errors }" name="jobTitle" rules="required">
      <v-text-field
        :error-messages="getErrorMessages(errors)"
        label="Subject"
        class="mb-4 mt-2"
        v-model="messageTemplateData.subject"
      />
    </ValidationProvider>
    <v-label>Body</v-label>
    <quill-editor
      v-model="messageTemplateData.body"
      :options="editorOptions"
      class="mb-4 mt-2"
      @ready="adjustVariablesTextContent()"
      ref="quillEditor"
    />
    <div v-if="showMessageBodyError" class="text-error">
      This field is required
    </div>
    <FileUpload
      class="mt-5"
      @unselectFile="unselectFile"
      @selectFile="selectAttachment"
      :files="messageTemplateData.attachments"
      showPreview
      @show="showFile"
      disableAutoProceed
      customActions
      isMultiple
    />
    <PDFViewFull :data="fileDialog" />
    <Select
      v-model="messageTemplateData.category"
      label="Category"
      item-text="label"
      item-value="value"
      :items="categoryItems"
      autocomplete
      chips
    >
      <template v-slot:selection="{ data }">
        <v-chip close @click:close="messageTemplateData.category = ''">
          {{ data.item.label }}
        </v-chip>
      </template>
    </Select>
    <ValidationProvider v-slot="{ errors }" rules="required">
      <Select
        v-model="messageTemplateData.tags"
        :items="tags"
        :loading="isFetchingTags"
        label="Tags"
        multiple
        :errorMessages="getErrorMessages(errors)"
      />
    </ValidationProvider>
  </ValidationObserver>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { createNamespacedHelpers } from "vuex";
import { DATA_NAMESPACE } from "@/store/modules/data";
import { DEFAULT_TOOLBAR_OPTIONS } from "@/plugins/v-quill-editor";
import {
  ALL_VARIABLES,
  adjustVariablesTextContent,
  adjustAlreadyInsertedVariables,
  insertVariable
} from "@/utils/conversations";
import { FETCH_TAGS, REMOVE_FILE } from "@/store/modules/data/actions";
import { GET_TAGS, GET_IS_FETCHING_TAGS } from "@/store/modules/data/getters";
import { remove, merge, map, filter } from "lodash";
import FileUpload from "@/components/common/FileUpload";
import { MESSAGE_TEMPLATE_CATEGORIES } from "@/constants/conversations";
import Select from "@/components/common/Select";
import { getFilePreviewSettings } from "@/utils/files";
import PDFViewFull from "@/components/common/PDFViewFull";
import { ORGANISATION_MESSAGE_TEMPLATES_NAMESPACE } from "@/store/modules/organisations/organisation-message-templates";
import { ADD_MESSAGE_TEMPLATE_FILE } from "@/store/modules/organisations/organisation-message-templates/actions";

const {
  mapActions: mapDataActions,
  mapGetters: mapDataGetters
} = createNamespacedHelpers(DATA_NAMESPACE);

const { mapActions: mapMessageTemplateActions } = createNamespacedHelpers(
  ORGANISATION_MESSAGE_TEMPLATES_NAMESPACE
);

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    FileUpload,
    Select,
    PDFViewFull
  },
  props: {
    messageTemplate: Object
  },
  data() {
    return {
      categoryItems: MESSAGE_TEMPLATE_CATEGORIES,
      messageTemplateData: {
        attachments: [],
        body: null,
        subject: "",
        category: "",
        tags: []
      },
      editorOptions: {
        modules: {
          toolbar: {
            container: [
              ...DEFAULT_TOOLBAR_OPTIONS,
              [
                {
                  "insert-variable": ALL_VARIABLES
                }
              ]
            ],
            handlers: {
              "insert-variable": val =>
                insertVariable(val, this.$refs.quillEditor.quill)
            }
          }
        }
      },
      fileDialog: { dialog: false },
      showValidationErrors: false,
      filesToBeRemoved: []
    };
  },
  created() {
    if (this.messageTemplate) {
      this.adjustMessageTemplateData();
    }
    this.fetchTags({
      filter: {
        taxonomy: "message_templates"
      }
    });
  },
  computed: {
    ...mapDataGetters({
      tags: GET_TAGS,
      isFetchingTags: GET_IS_FETCHING_TAGS
    }),
    showMessageBodyError() {
      return this.showValidationErrors && !this.messageTemplateData.body;
    }
  },
  methods: {
    ...mapDataActions({
      fetchTags: FETCH_TAGS,
      removeFile: REMOVE_FILE
    }),
    ...mapMessageTemplateActions({
      addMessageTemplateFile: ADD_MESSAGE_TEMPLATE_FILE
    }),
    adjustMessageTemplateData() {
      this.messageTemplateData = merge({}, this.messageTemplate);

      setTimeout(() => {
        adjustAlreadyInsertedVariables();
      }, 100);
    },
    getErrorMessages(errors) {
      return this.showValidationErrors ? errors : "";
    },
    selectAttachment({ file }) {
      this.messageTemplateData.attachments.push(file);
    },
    async showFile(file) {
      const filePreviewsettings = await getFilePreviewSettings(file);
      this.fileDialog = {
        ...filePreviewsettings,
        dialog: true
      };
    },
    unselectFile({ uppyInstance, file: fileToBeRemoved }) {
      if (fileToBeRemoved.url) {
        this.filesToBeRemoved.push(fileToBeRemoved);
      }
      remove(
        this.messageTemplateData.attachments,
        file => file.id === fileToBeRemoved.id
      );
      uppyInstance.reset();
    },
    getNewAttachments() {
      // return only new uploaded files, already uploaded attachments have url param
      return filter(this.messageTemplateData.attachments, ({ url }) => !url);
    },
    async uploadNewFiles(messageTemplateId) {
      const newAttachments = this.getNewAttachments();
      const requests = map(newAttachments, file => {
        const formData = new FormData();
        formData.append("file", file.data);
        return this.addMessageTemplateFile({
          id: messageTemplateId,
          data: formData
        });
      });
      return Promise.all(requests);
    },
    async validateForm() {
      this.showValidationErrors = true;
      const isFormValid = await this.$refs.messageTemplateForm.validate();
      return isFormValid && this.messageTemplateData.body;
    },
    async removeFiles() {
      const requests = map(this.filesToBeRemoved, ({ url }) =>
        this.removeFile(url)
      );
      return Promise.all(requests);
    },
    adjustVariablesTextContent
  }
};
</script>

<style lang="scss"></style>
